import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, useSprings } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import bezier from 'bezier-easing';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { Fragment, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { Rarity, Skin } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { AddCards } from '@sorare/core/src/atoms/icons/AddCards';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import { RotateLeft } from '@sorare/core/src/atoms/icons/RotateLeft';
import { Actions } from '@sorare/core/src/atoms/layout/Actions/index.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { CommonShards } from '@sorare/core/src/components/CommonShards';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { Card } from '@sorare/core/src/components/card/Card';
import { VideoAutoplay } from '@sorare/core/src/components/video/VideoAutoplay/index.tsx';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import {
  ensureHexColor,
  getRgbColorFromHex,
} from '@sorare/core/src/lib/colors.ts';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { glossary } from '@sorare/core/src/lib/glossary';
import { hapticFeedback } from '@sorare/core/src/lib/haptic';

import { PackRecap } from '#src/components/commonCard/RevealPackCardsDialog/PackRecap/index.tsx';
import {
  BaseLayout,
  BottomArea,
  CenterArea,
  TopArea,
} from '#src/components/commonCard/RevealPackCardsDialog/ui.ts';

import { CardBonusWithTransition } from './CardBonusWithTransition/index.tsx';
import { CardInfos } from './CardInfos/index.tsx';
import { Glow } from './Glow/index.tsx';
import { NewCardAnimation } from './NewCardAnimation/index.tsx';
import { NewCardTag } from './NewCardTag/index.tsx';
import type { TinderCardSwipe_anyCardInterface } from './__generated__/index.graphql.ts';

const CardImgWrapper = styled(animated.div)`
  position: absolute;
  top: 0;
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  user-select: none;
  touch-action: none;
  cursor: grab;
  /* Safari ignores z-index stacking contexts for blend isolation, so the foil mix-blend-mode bleeds onto cards behind without this. */
  isolation: isolate;
  & > img {
    user-select: none;
    touch-action: none;
  }
  &:focus,
  &:active {
    cursor: grabbing;
  }
`;
const CTAsWrapper = styled(SBHorizontal).attrs({ gap: 3 })`
  align-items: baseline;
  padding-bottom: calc(var(--unit) + 1em);

  ${Vertical} {
    position: relative;
    ${LabelS} {
      width: 100px;
      position: absolute;
      top: calc(100% + var(--unit));
    }
  }
`;
const CoinsBalanceWrapper = styled.div`
  padding: var(--double-unit) 0 0 var(--double-unit);
`;

const NewTagWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--half-unit));
  left: 0;
  transform: translateY(-100%);
`;
const NewCardAnimationWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  overflow: visible;
`;

const CardsWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
  aspect-ratio: var(--card-aspect-ratio);
  width: var(--card-width);
  max-width: 100%;
`;

const ScrapLabel = styled(LabelS)`
  white-space: nowrap;
`;

const messages = defineMessages({
  keepCard: {
    defaultMessage: 'Keep card',
  },
  scrapCard: {
    defaultMessage: 'Scrap card',
  },
  newPlayer: {
    defaultMessage: 'New player',
  },
  newEdition: {
    defaultMessage: 'New edition',
  },
});

const BackgroundVideo = styled(VideoAutoplay)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  object-fit: cover;
  object-position: center;
`;

const CardBonusWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(40%, -30%);
  z-index: 1;
`;

type Props = {
  cards: TinderCardSwipe_anyCardInterface[];
  cardSetSlug: string;
  onContinue: () => void;
  isFirstPack?: boolean;
  packId: string;
  droppedCards: DropCardInput[];
  setDroppedCards: Dispatch<SetStateAction<DropCardInput[]>>;
  packAnimationDone: boolean;
  showPlayerDetails?: boolean;
  recapContinueLoading?: boolean;
  displayRecap: boolean;
  setDisplayRecap: Dispatch<SetStateAction<boolean>>;
  onNextCard: (index: number) => void;
  header: ReactNode;
  onCardsClaimed?: () => void;
  isBlockchainPack?: boolean;
};

const initialSpringStyles = (index: number) => ({
  x: 0,
  scale: 1,
  rotateZ: 0,
  zIndex: -1 * index,
});

const ACTION_COMPLETED_SWIPE_THRESHOLD = 40;

export const TinderCardSwipe = ({
  cards,
  cardSetSlug,
  onContinue,
  isFirstPack,
  packId,
  droppedCards,
  setDroppedCards,
  packAnimationDone,
  showPlayerDetails,
  recapContinueLoading,
  displayRecap,
  setDisplayRecap,
  onNextCard,
  header,
  onCardsClaimed,
  isBlockchainPack,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  const isTabletAndAbove = useIsTabletAndAbove();
  const [commonCardShardsCount, setCommonCardShardsCount] = useState(
    getCommonCardShardsCount(currentUser, cardSetSlug) || 0
  );
  const brandingCardSet = cards.at(0)?.cardSet;
  const cardSetBranding = brandingCardSet?.branding;
  const [springStyles, api] = useSprings(cards.length, springIndex => ({
    from: initialSpringStyles(springIndex),
  }));
  const newCardAnimationPlaying = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [currentMovedIndex, setCurrentMovedIndex] =
    useState<Nullable<number>>(null);
  const isLaptopAndAbove = useIsLaptop();
  const centerAreaRef = useRef<HTMLDivElement>(null);

  const currentCardPrice =
    cards.at(currentIndex)?.signedDropCardPrice?.price || 0;

  const containerWidth = isLaptopAndAbove ? 440 : window.innerWidth;
  const swipedCardFinalOffset = containerWidth + 200;

  const currentCard = cards.at(currentIndex);

  const moveToNextIndex = () => {
    newCardAnimationPlaying.current = false;
    setCurrentIndex(prev => prev + 1);
    onNextCard(currentIndex + 1);
    if (currentIndex === cards.length - 1) {
      setDisplayRecap(true);
    }
  };

  const dropCurrentCard = () => {
    if (
      currentCard &&
      !droppedCards.some(c => c.cardSlug === currentCard.slug)
    ) {
      const { slug, signedDropCardPrice } = currentCard;

      if (signedDropCardPrice) {
        setCommonCardShardsCount(prev => prev + signedDropCardPrice.price);
        setDroppedCards(prev => [
          ...prev,
          { cardSlug: slug, signedPrice: signedDropCardPrice.signedPrice },
        ]);
      }
    }
  };

  const moveWithoutDragSpringStyles = ({
    direction,
  }: {
    direction: number;
  }) => ({
    x: direction * swipedCardFinalOffset,
    scale: 1.05,
    rotateZ: (direction * swipedCardFinalOffset) / 20,
    config: {
      tension: 100,
      friction: 50,
    },
  });

  const moveCardWithoutDrag = ({ isDrop }: { isDrop: boolean }) => {
    if (isDrop) {
      dropCurrentCard();
    }
    moveToNextIndex();

    const direction = isDrop ? -1 : 1;
    api.start(i => {
      if (currentIndex !== i) return {};
      return moveWithoutDragSpringStyles({ direction });
    });
  };

  const moveRemainingCards = () => {
    const remainingCardsCount = cards.length - currentIndex;
    onNextCard(cards.length - 1);
    setTimeout(
      () => {
        newCardAnimationPlaying.current = false;
        setCurrentIndex(cards.length - 1);
        requestAnimationFrame(() => {
          setDisplayRecap(true);
        });
      },
      200 + remainingCardsCount * 50
    );
    // start on next tick to update the currentIndex on the parent first
    setTimeout(() => {
      api.start(i => {
        if (i < currentIndex) return {};
        return {
          ...moveWithoutDragSpringStyles({ direction: 1 }),
          config: {
            ...moveWithoutDragSpringStyles({ direction: 1 }),
            friction: 50 + (i - currentIndex) * 150,
          },
          delay: (i - currentIndex) * 50,
        };
      });
    });
  };

  const undo = () => {
    const previousCard = cards.at(currentIndex - 1);
    const isPreviousCardDropped = droppedCards.some(
      c => c.cardSlug === previousCard?.slug
    );

    if (isPreviousCardDropped) {
      setDroppedCards(prev => prev.slice(0, -1));
      setCommonCardShardsCount(
        prev => prev - (previousCard?.signedDropCardPrice?.price || 0)
      );
    }

    api.start(i => {
      if (currentIndex - 1 !== i) return {};
      return {
        ...initialSpringStyles(i),
        config: {
          tension: 600,
          clamp: true,
        },
      };
    });

    if (currentIndex > 0) {
      newCardAnimationPlaying.current = false;
      setCurrentIndex(prev => prev - 1);
      onNextCard(currentIndex - 1);
    }
  };

  const bind = useDrag(
    ({
      args: [{ index }],
      movement: [mx],
      last,
      tap,
      initial: [, yInitial],
    }) => {
      const isGone = Math.abs(mx) > ACTION_COMPLETED_SWIPE_THRESHOLD || tap;
      const direction = mx > 0 || tap ? 1 : -1;

      // ignore consequent draggings of a card that is being swiped out
      if (currentIndex !== index) {
        return;
      }

      if (currentMovedIndex !== index) {
        setCurrentMovedIndex(index);
      }

      const { top, height } =
        centerAreaRef.current?.getBoundingClientRect() || { top: 0, height: 0 };
      const rotateFactor =
        ((-1 / 15) * (yInitial - top - height / 2)) / Math.max(height / 2, 1);

      api.start(i => {
        if (index !== i) return {};
        if (last || tap) {
          if (isGone) {
            moveToNextIndex();
            return moveWithoutDragSpringStyles({
              direction,
            });
          }
          setCurrentMovedIndex(null);
          return {
            ...initialSpringStyles(index),
            config: {
              tension: 150,
            },
          };
        }

        return {
          x: mx,
          scale: 1.05,
          rotateZ: mx * rotateFactor,
          config: {
            tension: 800,
            friction: 50,
          },
        };
      });
    }
  );

  if (displayRecap) {
    return (
      <PackRecap
        cards={cards}
        onContinue={onContinue}
        droppedCards={droppedCards}
        packId={packId}
        onBack={() => {
          setDisplayRecap(false);
          undo();
        }}
        isFirstPack={isFirstPack}
        onCardsClaimed={onCardsClaimed}
        continueLoading={recapContinueLoading}
        isBlockchainPack={isBlockchainPack}
      />
    );
  }

  const bumpNewCard = () => {
    api.start(i => {
      if (currentIndex !== i) return {};
      return {
        to: [
          {
            scale: 1.05,
            config: {
              duration: 133,
              easing: bezier(0.05, 0.88, 0.18, 0.99),
            },
          },
          {
            scale: 1,
            config: {
              duration: 233,
              easing: bezier(0.54, 0, 0.33, 1),
            },
          },
        ],
      };
    });
  };

  /* oxlint-disable react/refs */
  const showNewCardAnimation =
    packAnimationDone &&
    !!currentCard &&
    !currentCard.previouslyOwnedCard &&
    !newCardAnimationPlaying.current;

  if (showNewCardAnimation) {
    newCardAnimationPlaying.current = true;
    bumpNewCard();
  }

  const newEffectColor = cardSetBranding?.colors.newEffect
    ? getRgbColorFromHex(ensureHexColor(cardSetBranding.colors.newEffect))
    : null;

  const videoSourcesFromApi = [
    {
      src: currentCard?.webmCardBackgroundVideoUrl || '',
      type: 'video/webm; codecs="av01.0.31M.08,opus"',
    },
    { src: currentCard?.movCardBackgroundVideoUrl || '' },
  ];
  const cardBackGroundVideoSources = videoSourcesFromApi.find(
    ({ src }) => !!src
  )
    ? videoSourcesFromApi
    : null;

  return (
    <BaseLayout>
      {!!cardBackGroundVideoSources?.length && !!packAnimationDone && (
        <BackgroundVideo muted key={currentCard?.slug}>
          {cardBackGroundVideoSources.map((source, i) => {
            if (!source.src) {
              return null;
            }
            // oxlint-disable-next-line react/no-array-index-key
            return <source key={i} {...source} />;
          })}
        </BackgroundVideo>
      )}
      <TopArea
        left={
          isBlockchainPack ? undefined : (
            <CoinsBalanceWrapper>
              <EssenceBalance
                value={commonCardShardsCount}
                rarity={Rarity.common}
                cardSet={brandingCardSet}
              />
            </CoinsBalanceWrapper>
          )
        }
      >
        {header}
      </TopArea>
      <CenterArea ref={centerAreaRef}>
        <CardsWrapper>
          {cards.map((card, index) => {
            const isActiveCard =
              index === currentIndex && index !== currentMovedIndex;
            const showAnimation = isActiveCard && packAnimationDone;

            return (
              // oxlint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                <CardImgWrapper
                  {...bind({ index })}
                  style={{
                    ...springStyles[index],
                    zIndex: cards.length - index,
                  }}
                >
                  {!card.previouslyOwnedCard && (
                    <>
                      <NewTagWrapper>
                        <NewCardTag
                          show={isActiveCard}
                          message={
                            card.previouslyOwnedPlayer
                              ? messages.newEdition
                              : messages.newPlayer
                          }
                        />
                      </NewTagWrapper>
                      <NewCardAnimationWrapper>
                        <NewCardAnimation
                          show={showAnimation}
                          color={newEffectColor}
                        />
                      </NewCardAnimationWrapper>
                    </>
                  )}
                  <NewCardAnimationWrapper>
                    <Glow
                      show={showAnimation}
                      holo={card.skinEdition?.skin === Skin.HOLO}
                      color={cardSetBranding?.colors.main}
                    />
                  </NewCardAnimationWrapper>
                  {!!card.skinEdition &&
                    (card.skinEdition.bonus || 0) > 1 &&
                    !!showAnimation && (
                      <CardBonusWrapper>
                        <CardBonusWithTransition
                          skinEdition={card.skinEdition}
                        />
                      </CardBonusWrapper>
                    )}
                  <Card
                    width={320}
                    card={card}
                    disablefoilAnimation={index !== currentIndex}
                  />
                </CardImgWrapper>
              </Fragment>
            );
          })}
          {!!currentCard && (
            <CardInfos
              card={currentCard}
              showPlayerDetails={showPlayerDetails}
            />
          )}
        </CardsWrapper>
      </CenterArea>
      <BottomArea>
        <Vertical gap={2} center>
          {isBlockchainPack ? (
            <Button
              size="medium"
              color="quaternary"
              onClick={() => {
                hapticFeedback();
                moveCardWithoutDrag({ isDrop: false });
              }}
            >
              <FormattedMessage {...glossary.next} />
            </Button>
          ) : isFirstPack ? (
            <Actions direction="horizontal" gap={1}>
              <Button
                size="medium"
                fullWidth={!isTabletAndAbove}
                color="quaternary"
                onClick={moveRemainingCards}
              >
                <FormattedMessage {...glossary.skip} />
              </Button>
              <Button
                size="medium"
                fullWidth={!isTabletAndAbove}
                color="secondary"
                onClick={() => {
                  hapticFeedback();
                  moveCardWithoutDrag({ isDrop: false });
                }}
                disableDebounce
              >
                <FormattedMessage {...glossary.next} />
              </Button>
            </Actions>
          ) : (
            <CTAsWrapper>
              {!!currentCard && (
                <Vertical center>
                  <IconButton
                    size="large"
                    disableDebounce
                    color="destructive"
                    onClick={() => {
                      hapticFeedback();
                      moveCardWithoutDrag({ isDrop: true });
                    }}
                  >
                    <DropCards title={formatMessage(messages.scrapCard)} />
                  </IconButton>
                  <ScrapLabel className={'text-center'}>
                    <FormattedMessage
                      defaultMessage="Scrap for {amount} {essenceIcon}"
                      values={{
                        amount: currentCardPrice,
                        essenceIcon: (
                          <CommonShards cardSet={brandingCardSet} size={2} />
                        ),
                      }}
                    />
                  </ScrapLabel>
                </Vertical>
              )}
              <IconButton
                size="medium"
                disableDebounce
                color="tertiary"
                onClick={undo}
                disabled={currentIndex === 0}
              >
                <RotateLeft title={formatMessage(glossary.undo)} />
              </IconButton>
              {!!currentCard && (
                <IconButton
                  size="large"
                  disableDebounce
                  color="primary"
                  onClick={() => {
                    hapticFeedback();
                    moveCardWithoutDrag({ isDrop: false });
                  }}
                >
                  <AddCards title={formatMessage(messages.keepCard)} />
                </IconButton>
              )}
            </CTAsWrapper>
          )}
        </Vertical>
      </BottomArea>
    </BaseLayout>
  );
};

TinderCardSwipe.fragments = {
  anyCardInterface: gql`
    fragment TinderCardSwipe_anyCardInterface on AnyCardInterface {
      slug
      cardSet {
        slug
        branding {
          id
          colors {
            swipeEffect
            newEffect
            main
          }
        }
      }
      previouslyOwnedCard
      previouslyOwnedPlayer
      cardEditionName
      webmCardBackgroundVideoUrl: cardBackgroundVideoUrl
      movCardBackgroundVideoUrl: cardBackgroundVideoUrl(derivative: "mov")
      skinEdition {
        bonus
        ...CardBonusWithTransition_skinEdition
      }
      signedDropCardPrice {
        signedPrice
        price
      }
      ...CardInfos_anyCardInterface
      ...PackRecap_anyCardInterface
      ...Card_anyCard
    }
    ${CardInfos.fragments.anyCardInterface}
    ${Card.fragments.anyCard}
    ${PackRecap.fragments.anyCardInterface}
    ${CardBonusWithTransition.fragments.skinEdition}
  ` as TypedDocumentNode<TinderCardSwipe_anyCardInterface>,
};
